import * as THREE from "three";
import type { PlayerId, TargetId, TargetState, Vector3Like } from "@ict/shared";
import { TargetModel } from "./TargetModel";

interface ShardParams {
  radius: number;
  speed: number;
  phase: number;
  tilt: number;
  scale: number;
}

interface TargetEntry {
  record: TargetState;
  group: THREE.Group;
  model: TargetModel;
  ring: THREE.Mesh;
  shards: THREE.InstancedMesh;
  shardParams: ShardParams[];
  /** Invisible raycast proxy — the GLB creature loads asynchronously and is a
   * Group (no direct geometry), so combat targeting needs a stable hittable
   * mesh that exists from frame one. */
  hitbox: THREE.Mesh;
  destroyed: boolean;
  moveFrom: THREE.Vector3;
  moveTo: THREE.Vector3;
  moveT: number;
}

const OWN_COLOR = 0xffb020;
const OTHER_COLOR = 0x3a5f78;
const DESTROY_FADE_SECONDS = 1.1;
const SHARD_COUNT = 6;
const FLEE_INTERP_SECONDS = 0.15; // matches the server's flee-tick interval (150ms)
const shardDummy = new THREE.Object3D();

export class TargetsController {
  private entries = new Map<TargetId, TargetEntry>();
  private particleBursts: { points: THREE.Points; age: number }[] = [];

  constructor(private readonly scene: THREE.Scene, private myPlayerId: PlayerId) {}

  setMyPlayerId(id: PlayerId): void {
    this.myPlayerId = id;
  }

  setTargets(targets: TargetState[]): void {
    this.clear();
    for (const t of targets) this.addTarget(t);
  }

  private addTarget(record: TargetState): void {
    const isMine = record.ownerPlayerId === this.myPlayerId;
    const color = isMine ? OWN_COLOR : OTHER_COLOR;

    const group = new THREE.Group();
    group.position.set(record.position.x, record.position.y + 1.2, record.position.z);

    const model = new TargetModel(color, isMine ? 1.1 : 0.4);
    group.add(model.root);

    const hitbox = new THREE.Mesh(
      new THREE.SphereGeometry(0.7, 8, 8),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
    );
    group.add(hitbox);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.0, 0.04, 8, 32),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: isMine ? 0.9 : 0.35 })
    );
    ring.rotation.x = Math.PI / 2.4;
    group.add(ring);

    if (isMine) {
      const beam = new THREE.Mesh(
        new THREE.CylinderGeometry(0.02, 0.02, 40, 6, 1, true),
        new THREE.MeshBasicMaterial({ color: OWN_COLOR, transparent: true, opacity: 0.25 })
      );
      beam.position.y = 20;
      group.add(beam);
    }

    // Small orbiting data-shard fragments — reinforces the "corrupted data
    // creature" fantasy silhouette (spec section 9) beyond a plain glowing ball.
    const shards = new THREE.InstancedMesh(
      new THREE.TetrahedronGeometry(0.12, 0),
      new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.6, roughness: 0.3 }),
      SHARD_COUNT
    );
    const shardParams: ShardParams[] = Array.from({ length: SHARD_COUNT }, (_, i) => ({
      radius: 0.9 + (i % 3) * 0.15,
      speed: 0.6 + (i % 4) * 0.25,
      phase: (i / SHARD_COUNT) * Math.PI * 2,
      tilt: ((i * 37) % 100) / 100 - 0.5,
      scale: 0.7 + ((i * 53) % 100) / 200,
    }));
    group.add(shards);

    this.scene.add(group);
    this.entries.set(record.targetId, {
      record: { ...record },
      group,
      model,
      ring,
      shards,
      shardParams,
      hitbox,
      destroyed: false,
      moveFrom: new THREE.Vector3(record.position.x, record.position.y, record.position.z),
      moveTo: new THREE.Vector3(record.position.x, record.position.y, record.position.z),
      moveT: 1,
    });
  }

  /** Applies a server-authoritative position update (target fleeing after being hit) with smooth interpolation. */
  applyMove(targetId: TargetId, position: Vector3Like): void {
    const entry = this.entries.get(targetId);
    if (!entry) return;
    entry.moveFrom.set(entry.record.position.x, entry.record.position.y, entry.record.position.z);
    entry.moveTo.set(position.x, position.y, position.z);
    entry.record.position = position;
    entry.moveT = 0;
  }

  applyHit(targetId: TargetId, hp: number, maxHp: number): void {
    const entry = this.entries.get(targetId);
    if (!entry) return;
    entry.record.hp = hp;
    entry.record.maxHp = maxHp;
    entry.model.playHit();
    entry.model.setLoop("fleeing"); // server marks the target fleeing from its first hit onward
    const scaleFactor = 0.7 + 0.3 * (hp / Math.max(1, maxHp));
    entry.model.root.scale.setScalar(scaleFactor);
  }

  applyDestroyed(targetId: TargetId): void {
    const entry = this.entries.get(targetId);
    if (!entry || entry.destroyed) return;
    entry.destroyed = true;
    this.spawnBurst(entry.group.position.clone());
    this.scene.remove(entry.group);
    entry.model.dispose();
    entry.hitbox.geometry.dispose();
    (entry.hitbox.material as THREE.Material).dispose();
    entry.ring.geometry.dispose();
    (entry.ring.material as THREE.Material).dispose();
    entry.shards.geometry.dispose();
    (entry.shards.material as THREE.Material).dispose();
    this.entries.delete(targetId);
  }

  private spawnBurst(position: THREE.Vector3): void {
    const count = 40;
    const positions = new Float32Array(count * 3);
    const velocities: THREE.Vector3[] = [];
    for (let i = 0; i < count; i++) {
      positions[i * 3] = position.x;
      positions[i * 3 + 1] = position.y;
      positions[i * 3 + 2] = position.z;
      velocities.push(new THREE.Vector3((Math.random() - 0.5) * 4, Math.random() * 5, (Math.random() - 0.5) * 4));
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    (geo as unknown as { userData: { velocities: THREE.Vector3[] } }).userData = { velocities };
    const mat = new THREE.PointsMaterial({ color: OWN_COLOR, size: 0.12, transparent: true, opacity: 1 });
    const points = new THREE.Points(geo, mat);
    this.scene.add(points);
    this.particleBursts.push({ points, age: 0 });
  }

  update(dt: number, elapsed: number): void {
    for (const entry of this.entries.values()) {
      entry.model.update(dt);
      entry.ring.rotation.y = elapsed * 0.6; // halo spin — the creature keeps its own animated orientation

      if (entry.moveT < 1) {
        entry.moveT = Math.min(1, entry.moveT + dt / FLEE_INTERP_SECONDS);
        entry.group.position.x = THREE.MathUtils.lerp(entry.moveFrom.x, entry.moveTo.x, entry.moveT);
        entry.group.position.z = THREE.MathUtils.lerp(entry.moveFrom.z, entry.moveTo.z, entry.moveT);
      }
      entry.group.position.y = entry.record.position.y + 1.2 + Math.sin(elapsed * 1.5) * 0.08;

      entry.shardParams.forEach((p, i) => {
        const angle = elapsed * p.speed + p.phase;
        shardDummy.position.set(Math.cos(angle) * p.radius, Math.sin(angle * 0.7) * p.tilt, Math.sin(angle) * p.radius);
        shardDummy.rotation.set(angle, angle * 1.3, 0);
        shardDummy.scale.setScalar(p.scale);
        shardDummy.updateMatrix();
        entry.shards.setMatrixAt(i, shardDummy.matrix);
      });
      entry.shards.instanceMatrix.needsUpdate = true;
    }

    for (let i = this.particleBursts.length - 1; i >= 0; i--) {
      const burst = this.particleBursts[i];
      burst.age += dt;
      const geo = burst.points.geometry as THREE.BufferGeometry;
      const positions = geo.attributes.position as THREE.BufferAttribute;
      const velocities = (geo as unknown as { userData: { velocities: THREE.Vector3[] } }).userData.velocities;
      for (let p = 0; p < positions.count; p++) {
        const v = velocities[p];
        v.y -= 9 * dt;
        positions.setXYZ(p, positions.getX(p) + v.x * dt, positions.getY(p) + v.y * dt, positions.getZ(p) + v.z * dt);
      }
      positions.needsUpdate = true;
      const mat = burst.points.material as THREE.PointsMaterial;
      mat.opacity = Math.max(0, 1 - burst.age / DESTROY_FADE_SECONDS);
      if (burst.age >= DESTROY_FADE_SECONDS) {
        this.scene.remove(burst.points);
        geo.dispose();
        mat.dispose();
        this.particleBursts.splice(i, 1);
      }
    }
  }

  getOwnTargetId(): TargetId | null {
    for (const [id, e] of this.entries) {
      if (e.record.ownerPlayerId === this.myPlayerId) return id;
    }
    return null;
  }

  getPosition(targetId: TargetId): Vector3Like | null {
    const e = this.entries.get(targetId);
    return e ? { x: e.group.position.x, y: e.group.position.y, z: e.group.position.z } : null;
  }

  meshesForRaycast(): { targetId: TargetId; object: THREE.Object3D }[] {
    return [...this.entries.entries()].map(([targetId, e]) => ({ targetId, object: e.hitbox }));
  }

  distanceFromPlayer(targetId: TargetId, playerPos: Vector3Like): number | null {
    const e = this.entries.get(targetId);
    if (!e) return null;
    const dx = e.group.position.x - playerPos.x;
    const dz = e.group.position.z - playerPos.z;
    return Math.hypot(dx, dz);
  }

  clear(): void {
    for (const targetId of [...this.entries.keys()]) {
      const e = this.entries.get(targetId)!;
      this.scene.remove(e.group);
      e.model.dispose();
      e.hitbox.geometry.dispose();
      (e.hitbox.material as THREE.Material).dispose();
      e.ring.geometry.dispose();
      (e.ring.material as THREE.Material).dispose();
      e.shards.geometry.dispose();
      (e.shards.material as THREE.Material).dispose();
      this.entries.delete(targetId);
    }
  }

  dispose(): void {
    this.clear();
    for (const burst of this.particleBursts) {
      this.scene.remove(burst.points);
      burst.points.geometry.dispose();
      (burst.points.material as THREE.Material).dispose();
    }
    this.particleBursts = [];
  }
}
